import React from "react";
import {
	Box,
	Chip,
	LinearProgress,
	Paper,
	Skeleton,
	Tooltip,
	Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { ErrorOutlineRounded } from "@mui/icons-material";
import { useCentralAlertas } from "../context";
import useStyles from "./styles";
import { GUIDES_CENTRAL, MetricInfo } from "../metricInfo";

// Ranking dos até 5 equipamentos com mais alertas no período. O clique
// alterna o filtro de equipamento (que a rota real honra — o hostId do
// ranking é o mesmo id usado em filterEquipamentos).
const TopEquipamentos = () => {
	const theme = useTheme();
	const styles = useStyles(theme);
	const {
		carregandoAlertasPeriodo,
		rankingEquipamentos,
		filtros,
		aplicarFiltroEquipamento,
	} = useCentralAlertas();

	// Lista já vem ordenada desc por totalAlertas; o líder define a escala.
	const maxTotal = rankingEquipamentos.length
		? rankingEquipamentos[0].totalAlertas
		: 0;

	const estaFiltrado = (equipamentoId) =>
		filtros.equipamentos.length === 1 &&
		filtros.equipamentos[0] === equipamentoId;

	const renderConteudo = () => {
		if (carregandoAlertasPeriodo) {
			return (
				<Box sx={styles.lista}>
					{[0, 1, 2, 3, 4].map((indice) => (
						<Box key={indice} sx={styles.skeletonItem}>
							<Skeleton variant="text" width="60%" />
							<Skeleton variant="text" width="40%" />
							<Skeleton variant="rounded" height={6} width="100%" />
						</Box>
					))}
				</Box>
			);
		}

		if (!rankingEquipamentos.length) {
			return (
				<Box sx={styles.estadoVazio}>
					<Typography color="text.secondary">
						Nenhum incidente no período selecionado.
					</Typography>
				</Box>
			);
		}

		return (
			<Box sx={styles.lista}>
				{rankingEquipamentos.map((item, indice) => {
					const equipamentoId = item.equipamento?.id;
					const filtrado = estaFiltrado(equipamentoId);
					return (
						<Tooltip
							key={equipamentoId ?? indice}
							title={
								filtrado
									? "Clique para remover o filtro"
									: "Filtrar por este equipamento"
							}
							arrow
						>
							<Box
								sx={styles.item(filtrado)}
								onClick={() => aplicarFiltroEquipamento(equipamentoId)}
							>
								<Box sx={styles.linhaSuperior}>
									<Typography variant="body2" sx={styles.nomeEquipamento}>
										{item.equipamento?.nome || "—"}
									</Typography>
									<Box sx={styles.boxContagem}>
										<Typography variant="caption" sx={styles.contagem}>
											{`${item.totalAlertas} ${
												item.totalAlertas === 1 ? "alerta" : "alertas"
											}`}
										</Typography>
										{item.incidentesAbertos > 0 && (
											<Chip
												size="small"
												icon={<ErrorOutlineRounded />}
												label={`${item.incidentesAbertos} ${
													item.incidentesAbertos === 1
														? "aberto"
														: "abertos"
												}`}
												sx={styles.chipAbertos}
											/>
										)}
									</Box>
								</Box>
								<Typography variant="caption" sx={styles.nomeCliente}>
									{item.cliente?.nome || "—"}
								</Typography>
								<LinearProgress
									variant="determinate"
									value={
										maxTotal ? (item.totalAlertas / maxTotal) * 100 : 0
									}
									sx={styles.barraProgresso}
								/>
							</Box>
						</Tooltip>
					);
				})}
			</Box>
		);
	};

	return (
		<Paper sx={styles.container}>
			<Box sx={styles.linhaCabecalho}>
				<Box sx={styles.cabecalho}>
					<Typography variant="mySubtitle">Top equipamentos</Typography>
					<Typography variant="caption" sx={styles.caption}>
						Mais alertas no período
					</Typography>
				</Box>
				<MetricInfo theme={theme} guide={GUIDES_CENTRAL.topEquipamentos} />
			</Box>
			{renderConteudo()}
		</Paper>
	);
};

export default TopEquipamentos;
